import React, { useEffect, useState, useMemo } from 'react';
import {
    Modal,
    Box,
    IconButton,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Tooltip,
    TextField,
    FormControlLabel,
    Checkbox,
    Chip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Iframe from 'react-iframe';
import NoDocumentsPdf from '../../../assests/pdf/NoDocument.pdf';
import { useTranslation } from 'react-i18next';
import EnumsService from '../../../Services/enums.service';
import DocumentService from '../../../Services/documents.service';
import Constants from '../../../assests/Constants/constants';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { getVatValue } from '../../../Utils/GetVatValue.js';
import { styled, alpha } from '@mui/material/styles';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker.js';
import AutocompleteSelectFilter from '../../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter';
import fieldStyles from '../../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import {
    buildSourceAllocations,
    formatLinkAmount,
    getDocumentAvailableAmount,
    getProcessingStatus,
    getStatusClassName,
    getStatusLabelKey,
    INVOICE_RECEIPT_TYPE,
    INVOICING_GENERATE_ACTIONS,
    LIFECYCLE_STATUS,
    pairRemainingLines,
    projectGenerateAllocation,
    RECEIPT_TYPE,
    REMAINING_ITEMS_SOURCE_TYPES,
    roundAmount,
    TAX_INVOICE_TYPE,
} from '../documentLifecycle.js';

/** Payment line buckets of a Receipt / InvoiceReceipt, in display order. */
const PAYMENT_GROUPS = [
    { key: 'checks', type: 'check', labelKey: 'PaymentMethodCheck' },
    { key: 'creditCards', type: 'creditCard', labelKey: 'PaymentMethodCreditCard' },
    { key: 'cash', type: 'cash', labelKey: 'PaymentMethodCash' },
    { key: 'bankTransactions', type: 'bankTransaction', labelKey: 'PaymentMethodBankTransfer' },
    { key: 'offsets', type: 'offset', labelKey: 'PaymentMethodOffset' },
];

const describePaymentLine = (line) => {
    const bank = [line.bankCode, line.bankBranchCode].filter(Boolean).join('-');
    return [line.bankName, bank, line.supplierAccountNumber, line.reference2, line.description]
        .filter(Boolean)
        .join(' · ');
};

const lineNetAmount = ({ price = 0, discount = 0, quantity = 0 }) => {
    const numericPrice = Number(price) || 0;
    const numericDiscount = Number(discount) || 0;
    const numericQuantity = Number(quantity) || 0;
    return (numericPrice - (numericPrice * numericDiscount) / 100) * numericQuantity;
};

const isTaxableLine = (item) => item?.includeVat !== false && item?.IncludeVat !== false;

/** VAT rate (e.g. 0.18) of a document on the given date; 0 when the document carries no VAT. */
const getDocumentVatRate = (doc, date) => {
    if (doc?.vat === 0 || doc?.vat === '0') return 0;
    const vatMarker = Number(doc?.vat);
    const isPal = vatMarker === 16 || vatMarker === 17;
    return getVatValue(date || doc?.documentDate || new Date(), false, isPal);
};

/** Payable amount of lines: as-is when prices include VAT, else VAT added on taxable lines only. */
const sumPayableLines = (items, priceIncludingVat, vatRate) => (items || []).reduce((sum, item) => {
    const net = lineNetAmount(item);
    if (priceIncludingVat || !isTaxableLine(item) || !(vatRate > 0)) return sum + net;
    return sum + net * (1 + vatRate);
}, 0);

/**
 * Whether the source document was priced including VAT, read from its DocumentItem snapshots
 * against its amount (the document stores no such flag). null when neither reading matches.
 */
const detectSnapshotPriceIncludingVat = (items, docAmount, vatRate) => {
    const amount = Math.abs(Math.round((Number(docAmount) || 0) * 100) / 100);
    if (!items?.length || !amount) return null;
    const asInclusive = Math.abs(Math.round(sumPayableLines(items, true, vatRate) * 100) / 100);
    const asExclusive = Math.abs(Math.round(sumPayableLines(items, false, vatRate) * 100) / 100);
    const eps = Math.max(0.05, amount * 0.001);
    if (Math.abs(asInclusive - amount) <= eps) return true;
    if (Math.abs(asExclusive - amount) <= eps) return false;
    return null;
};

const RECEIPT_GENERATE_ACTIONS = new Set(['generate_receipt', 'generate_receipt_deferred']);
/** Generate actions where qty / discount / add / remove lines are editable (unit price locked). */
const EDITABLE_ITEMS_ACTIONS = new Set([
    'generate_invoice',
    'generate_invoice_receipt',
    'generate_order',
    'generate_transaction',
    'generate_quotation',
]);
const AMOUNT_TOLERANCE = 0.01;
/** Show at most this many item rows; more rows scroll inside a fixed table height. */
const ITEMS_TABLE_VISIBLE_ROWS = 3;
const ITEMS_TABLE_HEAD_PX = 40;
const ITEMS_TABLE_ROW_PX = 56;
const ITEMS_TABLE_MAX_HEIGHT = ITEMS_TABLE_HEAD_PX + ITEMS_TABLE_VISIBLE_ROWS * ITEMS_TABLE_ROW_PX;

const themedScrollbarSx = {
    scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
    scrollbarColor: '#d8daf2 transparent',
    '&::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#d8daf2',
        borderRadius: 'var(--xtram-radius-pill, 999px)',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: '#b9bde6',
    },
};

const remainingStatTileSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
    px: 1.25,
    py: 1,
    borderRadius: 2,
    backgroundColor: '#fff',
    border: '1px solid #FDE6CC',
    minWidth: 0,
};

const itemsTableScrollBaseSx = {
    width: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
    ...themedScrollbarSx,
    // Keep sticky header above AutocompleteSelectFilter stacking (z-index on wrap).
    '& .MuiTableCell-head': {
        position: 'sticky',
        top: 0,
        zIndex: 4,
        backgroundColor: '#F5F7FF !important',
    },
    '& .MuiTableCell-body': {
        position: 'relative',
        zIndex: 0,
    },
    '& .MuiTableCell-body > div': {
        zIndex: '0 !important',
    },
    '& .MuiInputBase-root': {
        minHeight: '32px !important',
        height: '32px !important',
    },
};

const StyledTable = styled(Table)(({ theme }) => ({
    width: '100%',
    minWidth: 640,
    tableLayout: 'fixed',
    borderCollapse: 'separate',
    borderSpacing: 0,
    '& .MuiTableCell-head': {
        backgroundColor: '#F5F7FF',
        color: theme.palette.text.primary,
        fontWeight: 600,
        fontSize: '0.8125rem',
        lineHeight: 1.25,
        padding: theme.spacing(0.75, 0.5),
        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        verticalAlign: 'middle',
    },
    '& .MuiTableCell-body': {
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        padding: theme.spacing(0.5, 0.5),
        fontSize: '0.75rem',
        verticalAlign: 'middle',
        overflow: 'hidden',
    },
    '& .MuiTableRow-root:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
    },
}));

function GenerateDocumentModal({
    open,
    onClose,
    doc,
    sourceDocs = [],
    conversionChildren = [],
    docItems,
    clientItems,
    onGenerate,
    pendingAction,
    client,
    token,
    showNoNeedConfirmationCheckbox = false,
}) {

    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const [receiptItems, setReceiptItems] = useState({});
    const [withholdingTax, setWithholdingTax] = useState(0);
    const [selectedDocumentDate, setSelectedDocumentDate] = useState(null);
    const [description, setDescription] = useState('');
    const [noNeedforConfirmationNumber, setNoNeedforConfirmationNumber] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [alsoCancelSourceDocuments, setAlsoCancelSourceDocuments] = useState(false);
    /** Editable receipt amount when generating from a (possibly partly paid) TaxInvoice. */
    const [linkAmountInput, setLinkAmountInput] = useState('');
    /** Local editable copy of document lines (quantity / discount / price) for TaxInvoice generation. */
    const [editableItems, setEditableItems] = useState([]);

    // Info about the account card (email, phone, etc.)
    const [accountCardInfo, setAccountCardInfo] = useState(null);

    const itemsEditable = EDITABLE_ITEMS_ACTIONS.has(pendingAction?.value);

    const selectableClientItems = useMemo(
        () => (clientItems || []).filter((ci) => ci
            && ci.makat !== 'add-item'
            && ci.id !== 'add-item'
            && String(ci.makat || '').toLowerCase() !== 'add-item'),
        [clientItems]
    );

    const inventoryFilterOptions = useMemo(
        () => selectableClientItems.map((ci) => {
            const makat = ci.makat || '';
            const name = ci.name || '';
            const label = [makat, name].filter(Boolean).join(' - ');
            return {
                ...ci,
                value: ci.id ?? ci.itemId ?? makat,
                label: label || String(ci.id ?? ''),
            };
        }),
        [selectableClientItems]
    );

    const comboInputProps = useMemo(() => ({ disableUnderline: true }), []);

    const activeSourceDocs = useMemo(() => {
        if (sourceDocs?.length > 1) return sourceDocs;
        if (doc?.sourceDocuments?.length > 1) return doc.sourceDocuments;
        return [];
    }, [sourceDocs, doc?.sourceDocuments]);

    const isCancelAction = useMemo(() => {
        const value = pendingAction?.value;
        if (!value) return false;
        if (String(value).startsWith('cancel_')) return true;
        return value === 'generate_credit_invoice' && Number(doc?.transactionDocumentType) === 4;
    }, [pendingAction?.value, doc?.transactionDocumentType]);

    const liveConversionChildren = useMemo(
        () => (conversionChildren || []).filter((child) => !child?.isCanceled),
        [conversionChildren]
    );

    const showAlsoCancelSources = isCancelAction && liveConversionChildren.length > 0;

    const isMultiSource = activeSourceDocs.length > 1;

    /* Partly invoiced source: only what is still to invoice. */
    const tracksRemaining = INVOICING_GENERATE_ACTIONS.has(pendingAction?.value)
        && !isMultiSource
        && !isCancelAction
        && REMAINING_ITEMS_SOURCE_TYPES.has(Number(doc?.transactionDocumentType));
    const [remainingInfo, setRemainingInfo] = useState(null);
    useEffect(() => {
        setRemainingInfo(null);
        if (!open || !tracksRemaining || !doc?.id) return undefined;
        let alive = true;
        DocumentService.GetDocumentRemainingItems(client?.caseId ?? doc.caseId, doc.id, token)
            .then((data) => { if (alive) setRemainingInfo(data); })
            .catch((err) => console.error('Failed to fetch remaining items', err));
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, doc?.id, pendingAction?.value, tracksRemaining]);

    const isPartlyInvoiced = (remainingInfo?.invoicedByDocumentIds?.length ?? 0) > 0;
    const invoicedAmount = remainingInfo?.remainingAmount != null && remainingInfo?.originalAmount != null
        ? roundAmount(Math.max(0, remainingInfo.originalAmount - remainingInfo.remainingAmount))
        : null;
    const fullyInvoicedItems = useMemo(
        () => (isPartlyInvoiced ? pairRemainingLines(docItems, remainingInfo?.lines) : [])
            .map((line, index) => (line && Number(line.remainingQuantity) <= 0 ? { ...docItems[index], line } : null))
            .filter(Boolean),
        [isPartlyInvoiced, docItems, remainingInfo]
    );

    const sourceDocumentsLabelKey = useMemo(() => {
        if (!isMultiSource) return 'SourceDeliveryNotes';
        const sourceType = Number(activeSourceDocs[0]?.transactionDocumentType);
        if (sourceType === 1) return 'SourceQuotations';
        if (sourceType === 2) return 'SourceOrders';
        if (sourceType === 3) return 'SourceTransactionAccounts';
        if (sourceType === 4) return 'SourceInvoices';
        if (sourceType === 11) return 'SourceDeliveryNotes';
        return 'SourceDocuments';
    }, [isMultiSource, activeSourceDocs]);

    const previewDocs = useMemo(() => {
        if (isMultiSource) return activeSourceDocs;
        return doc ? [doc] : [];
    }, [isMultiSource, activeSourceDocs, doc]);

    const currentPreviewDoc = previewDocs[previewIndex] ?? previewDocs[0] ?? doc;
    const previewUrl = currentPreviewDoc?.documentUrl
        ? `${currentPreviewDoc.documentUrl}#page=1&view=FitH`
        : `${NoDocumentsPdf}#page=1&view=FitH`;

    const isReceiptGenerate = RECEIPT_GENERATE_ACTIONS.has(pendingAction?.value);

    const referenceAmount = useMemo(() => {
        if (isMultiSource) {
            // Working doc amount is already the merged payable total from source docs.
            if (doc?.amount != null && Number(doc.amount) > 0) {
                return Number(doc.amount) || 0;
            }
            return activeSourceDocs.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
        }
        return Number(doc?.amount) || 0;
    }, [isMultiSource, activeSourceDocs, doc?.amount]);

    /** Remaining free on the source TaxInvoice(s) — what a new receipt may still collect. */
    const availableAmount = useMemo(() => {
        if (!isReceiptGenerate) return roundAmount(referenceAmount);
        if (isMultiSource) {
            const fromSources = roundAmount(
                activeSourceDocs.reduce((sum, d) => sum + getDocumentAvailableAmount(d), 0)
            );
            if (fromSources > 0) return fromSources;
            return roundAmount(getDocumentAvailableAmount(doc) || referenceAmount);
        }
        return roundAmount(getDocumentAvailableAmount(doc) || referenceAmount);
    }, [isReceiptGenerate, isMultiSource, activeSourceDocs, doc, referenceAmount]);

    const showAllocationSummary = isReceiptGenerate
        && (
            Number(doc?.transactionDocumentType) === TAX_INVOICE_TYPE
            || activeSourceDocs.some((d) => Number(d?.transactionDocumentType) === TAX_INVOICE_TYPE)
            || Math.abs(availableAmount - roundAmount(referenceAmount)) > AMOUNT_TOLERANCE
            || getProcessingStatus(doc) === LIFECYCLE_STATUS.PARTIAL
        );

    const linkAmount = roundAmount(linkAmountInput);
    const allocationProjection = useMemo(
        () => (isReceiptGenerate ? projectGenerateAllocation(availableAmount, linkAmount) : null),
        [isReceiptGenerate, availableAmount, linkAmount]
    );
    const linkAmountError = isReceiptGenerate && (
        linkAmount <= 0
        || linkAmount > availableAmount + AMOUNT_TOLERANCE
    );

    const displayReference = doc?.reference;

    const computeItemTotals = (
        items,
        docAmount,
        withholdingTaxValue,
        multiSource = false,
        { priceIncludingVat: editedPriceIncludingVat = true, vatRate: editedVatRate = 0 } = {},
    ) => {
        const EPS = 0.01;
        const numericWithholdingTax = Number(withholdingTaxValue) || 0;

        const itemsTotalWithoutVatRaw =
            items?.reduce((sum, { price = 0, discount = 0, quantity = 0 }) => {
                const numericPrice = Number(price) || 0;
                const numericDiscount = Number(discount) || 0;
                const numericQuantity = Number(quantity) || 0;
                const linePrice = numericPrice - (numericPrice * numericDiscount) / 100;
                return sum + linePrice * numericQuantity;
            }, 0) ?? 0;

        const itemsTotalWithoutVatRounded = Math.round(itemsTotalWithoutVatRaw * 100) / 100;

        // When editing lines (TaxInvoice generate), follow the item sum — do not lock to the
        // source document amount (otherwise qty 1→2 would keep showing the old total). The lines
        // keep the source document's pricing: when it excluded VAT, VAT is added per taxable line.
        if (docAmount == null) {
            const itemsTotal = Math.round(
                sumPayableLines(items, editedPriceIncludingVat, editedVatRate) * 100
            ) / 100;
            const netTotalAfterWithholding =
                Math.round((itemsTotal - numericWithholdingTax) * 100) / 100;
            return {
                itemsTotalWithoutVat: itemsTotalWithoutVatRounded,
                expectedTotalInclVat: itemsTotal,
                itemsAlreadyIncludeVat: editedPriceIncludingVat,
                itemsTotal,
                netTotalAfterWithholding,
            };
        }

        const numericDocAmount = Number(docAmount) || 0;

        // Credit documents (credit notes, cancellations) carry a NEGATIVE document amount while
        // their line prices stay positive, so the comparison below is done on magnitudes only.
        const documentSign = numericDocAmount < 0 ? -1 : 1;
        // The document amount is ALREADY the VAT-inclusive gross (e.g. 21,594 for an 18,300 line
        // plus 18% VAT); withholding tax is a deduction applied AFTER VAT and does not change
        // whether the line prices include VAT. Adding it here inflated the ratio past one VAT rate
        // (21,594 → 22,673.7 against an 18,300 line = 1.239), so a "price excludes VAT" invoice-
        // receipt was misread as VAT-inclusive and its cancel/refund invoice came out at the net
        // (18,300) instead of the gross (21,594). Keep withholding out of the VAT detection and the
        // total; it only affects the net-after-withholding line below.
        const expectedTotalMagnitude = Math.round(Math.abs(numericDocAmount) * 100) / 100;
        const expectedTotalInclVat = Math.round(documentSign * expectedTotalMagnitude * 100) / 100;

        // Detect whether the item prices already include VAT by comparing the raw item sum
        // to the document's payable amount. When prices INCLUDE VAT the two are ~equal; when
        // prices EXCLUDE VAT the amount is larger by the VAT rate (~17-18%).
        //
        // Lawyer invoice-receipts add non-VAT lines (deposits/expenses/fees) into the PA
        // amount while DocumentItems only store taxable invoice lines. That makes amount >>
        // items sum — do NOT treat that gap as "prices exclude VAT" (which wrongly adds 18%).
        // Only treat as excluding VAT when the gap is approximately one VAT rate.
        //
        // NOTE: a previous integer-rounding comparison (roundToIntHalfUp) collapsed small
        // amounts to the same integer (e.g. 1 vs 1.18 both -> 1), so refunds of low-value
        // "price excludes VAT" documents were mis-detected as VAT-inclusive and generated
        // with the wrong total (1.00 instead of 1.18). A proportional tolerance well below
        // the VAT rate distinguishes the two cases reliably at any magnitude.
        const itemsTotalMagnitude = Math.abs(itemsTotalWithoutVatRounded);
        const absoluteDifference = Math.abs(itemsTotalMagnitude - expectedTotalMagnitude);
        const referenceMagnitude = Math.max(itemsTotalMagnitude, expectedTotalMagnitude);
        const tolerance = Math.max(EPS, referenceMagnitude * 0.02);
        const ratio = itemsTotalMagnitude > 0
            ? expectedTotalMagnitude / itemsTotalMagnitude
            : 1;
        // Typical Israeli VAT factor is ~1.17–1.18; allow a small band around that.
        const looksLikeVatOnlyGap = ratio >= 1.10 && ratio <= 1.22;
        // Multi-source items are already normalized to VAT-inclusive amounts per source doc.
        const itemsAlreadyIncludeVat =
            multiSource || absoluteDifference <= tolerance || !looksLikeVatOnlyGap;

        // Prefer the normalized items sum; fall back to document amount only when items are empty.
        const itemsTotal = itemsAlreadyIncludeVat
            ? (Math.round(documentSign * itemsTotalMagnitude * 100) / 100 || expectedTotalInclVat)
            : expectedTotalInclVat;

        const netTotalAfterWithholding =
            Math.round((itemsTotal - documentSign * numericWithholdingTax) * 100) / 100;

        return {
            itemsTotalWithoutVat: itemsTotalWithoutVatRounded,
            expectedTotalInclVat,
            itemsAlreadyIncludeVat,
            itemsTotal,
            netTotalAfterWithholding,
        };
    };

    // /**
    //  * 1.  Sum the line items (still called “…WithoutVat” because it may or may not
    //  *     already contain VAT – we’ll detect that next).
    //  */
    // const itemsTotalWithoutVat =
    //     docItems?.reduce((sum, { price = 0, discount = 0, quantity = 0 }) => {
    //         const linePrice = price - (price * discount) / 100; // price after discount
    //         return sum + linePrice * quantity;
    //     }, 0) ?? 0;

    // /**
    //  * 2.  The amount you expect the document to show when VAT is *definitely*
    //  *     included.  ➜  If you also need to add the withholding tax, add it once
    //  *     here so every comparison works off the same figure.
    //  */
    // const expectedTotalInclVat =
    //     (doc?.amount ?? 0) + (withholdingTax ?? 0);

    // /**
    //  * 3.  Decide if the item sum already *contains* VAT.
    //  *     We use a small epsilon so 100 ≈ 100.0001 after rounding.
    //  */
    // const EPS = 0.01;
    // const itemsAlreadyIncludeVat =
    //     Math.abs(Math.abs(itemsTotalWithoutVat) - Math.abs(expectedTotalInclVat)) < EPS;

    // /**
    //  * 4.  Force the final total to be “including VAT”.
    //  */
    // const itemsTotal = itemsAlreadyIncludeVat
    //     ? itemsTotalWithoutVat                      // items were already gross
    //     : expectedTotalInclVat;                    // items were net ➜ use gross


    // const netTotalAfterWithholding = itemsTotal - (withholdingTax ?? 0);

    // How the source lines were priced (incl./excl. VAT), read from the source DocumentItem
    // snapshots vs the source amount; the edited lines must keep that pricing.
    const editedPricing = useMemo(() => {
        const vatRate = getDocumentVatRate(doc, selectedDocumentDate);
        if (isMultiSource) return { priceIncludingVat: true, vatRate };
        const sourceVatRate = getDocumentVatRate(doc);
        const fromSnapshots = detectSnapshotPriceIncludingVat(docItems, doc?.amount, sourceVatRate);
        const priceIncludingVat = fromSnapshots ?? (
            Number(doc?.amount)
                ? computeItemTotals(docItems || [], doc.amount, 0).itemsAlreadyIncludeVat
                : true
        );
        return { priceIncludingVat, vatRate };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docItems, doc, isMultiSource, selectedDocumentDate]);

    const activeItems = itemsEditable ? editableItems : (docItems || []);

    const docType = Number(doc?.transactionDocumentType);
    const showPaymentLines = isCancelAction && (docType === RECEIPT_TYPE || docType === INVOICE_RECEIPT_TYPE);
    const showItemLines = !(isCancelAction && docType === RECEIPT_TYPE);
    const paymentRows = useMemo(
        () => PAYMENT_GROUPS.flatMap(({ key, labelKey }) => (receiptItems?.[key] || []).map((line, index) => ({
            ...line,
            _key: `${key}-${line.id || index}`,
            labelKey,
        }))),
        [receiptItems]
    );
    const paymentsTotal = roundAmount(paymentRows.reduce((sum, line) => sum + (Number(line.amount) || 0), 0));
    // null docAmount ⇒ computeItemTotals uses the live line sum (needed while editing qty/price).
    const workingAmount = isReceiptGenerate
        ? (linkAmount || availableAmount)
        : (itemsEditable ? null : referenceAmount);

    const {
        itemsTotalWithoutVat,
        expectedTotalInclVat,
        itemsAlreadyIncludeVat,
        itemsTotal,
        netTotalAfterWithholding,
    } = useMemo(
        () => computeItemTotals(
            activeItems,
            workingAmount,
            withholdingTax,
            isMultiSource,
            editedPricing,
        ),
        [activeItems, workingAmount, withholdingTax, isMultiSource, editedPricing]
    );

    const displayAmount = isReceiptGenerate
        ? (linkAmount || availableAmount)
        : (itemsEditable ? (itemsTotal || referenceAmount) : (isMultiSource ? (itemsTotal || referenceAmount) : referenceAmount));

    const updateEditableItem = (index, patch) => {
        setEditableItems((prev) => prev.map((item, i) => {
            if (i !== index) return item;
            const next = { ...item, ...patch };
            if (Object.prototype.hasOwnProperty.call(patch, 'quantity')) {
                if (patch.quantity === '' || patch.quantity === null) {
                    next.quantity = '';
                } else {
                    let qty = Number(patch.quantity);
                    if (Number.isNaN(qty) || qty < 0) qty = 0;
                    next.quantity = qty;
                }
            }
            if (Object.prototype.hasOwnProperty.call(patch, 'price')) {
                if (patch.price === '' || patch.price === null) {
                    next.price = '';
                } else {
                    let price = Number(patch.price);
                    if (Number.isNaN(price) || price < 0) price = 0;
                    next.price = price;
                }
            }
            if (Object.prototype.hasOwnProperty.call(patch, 'discount')) {
                if (patch.discount === '' || patch.discount === null) {
                    next.discount = '';
                } else {
                    let discount = Number(patch.discount);
                    if (Number.isNaN(discount) || discount < 0) discount = 0;
                    if (discount > 100) discount = 100;
                    next.discount = discount;
                }
            }
            return next;
        }));
    };

    const addEditableItem = () => {
        setEditableItems((prev) => [
            ...prev,
            {
                _key: `new-${Date.now()}-${prev.length}`,
                itemId: null,
                makat: '',
                name: '',
                quantity: 1,
                price: 0,
                discount: 0,
                includeVat: true,
            },
        ]);
    };

    const removeEditableItem = (index) => {
        setEditableItems((prev) => prev.filter((_, i) => i !== index));
    };

    const applyClientItemToRow = (index, clientItem) => {
        if (!clientItem) {
            updateEditableItem(index, { itemId: null, makat: '', name: '', price: 0, _remaining: null });
            return;
        }
        updateEditableItem(index, {
            _remaining: null,
            itemId: clientItem.id ?? clientItem.itemId ?? clientItem.value ?? null,
            makat: clientItem.makat || '',
            name: clientItem.name || '',
            price: Number(clientItem.price) || 0,
            quantity: 1,
            discount: 0,
            includeVat: clientItem.includeVat !== false,
        });
    };

    const resolveInventoryFilterValue = (item) => {
        if (!item) return null;
        const matched = inventoryFilterOptions.find(
            (o) => String(o.value) === String(item.itemId)
                || (item.makat && String(o.makat) === String(item.makat))
        );
        if (matched) return matched;
        if (!(item.makat || item.name)) return null;
        const label = [item.makat, item.name].filter(Boolean).join(' - ');
        return {
            value: item.itemId || item.id || item._key,
            label,
            id: item.itemId || item.id,
            makat: item.makat || '',
            name: item.name || '',
            price: item.price,
        };
    };

    useEffect(() => {
        if (!open) return;

        const now = new Date();
        setSelectedDocumentDate(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
        setDescription('');
        setNoNeedforConfirmationNumber(false);
        setReceiptItems({});
        setWithholdingTax(0);
        setPreviewIndex(0);
        setAlsoCancelSourceDocuments(false);
        const pairs = isPartlyInvoiced ? pairRemainingLines(docItems, remainingInfo?.lines) : null;
        const cloned = (docItems || []).map((item, index) => {
            const remaining = pairs?.[index] || null;
            return {
                ...item,
                _key: `src-${index}-${item.id || item.itemId || item.makat || 'row'}`,
                _remaining: remaining,
                quantity: remaining ? Number(remaining.remainingQuantity) : (item.quantity ?? 1),
                price: item.price ?? 0,
                discount: item.discount ?? 0,
            };
        });
        // A partly invoiced source starts from what is still to invoice; fully invoiced lines are listed apart.
        setEditableItems(cloned.filter((row) => !row._remaining || Number(row._remaining.remainingQuantity) > 0));
        // Reset only when the modal opens for another document / action (or the remaining
        // quantities arrive), never while the user edits lines.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, doc?.id, pendingAction?.value, remainingInfo]);

    useEffect(() => {
        if (!open) return;
        setLinkAmountInput(availableAmount > 0 ? String(availableAmount) : '');
    }, [open, doc?.id, availableAmount, pendingAction?.value]);

    useEffect(() => {
        const fetchCardData = async () => {
            if (doc?.accountCodeId && client?.caseId) {
                try {
                    const res = await EnumsService.GetAccountCardByCardId(client.caseId, doc.accountCodeId, token);
                    const data = await res.json();

                    if (res.ok) {
                        setAccountCardInfo(data || {});
                    }
                } catch (err) {
                    console.error('Failed to fetch account card info', err);
                }
            }
        };
        fetchCardData();
    }, [doc, client, token]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    };

    /* --------------------------------------------------------------------- */
    /*  Fetch receipt‑items when doc is a Receipt / InvoiceReceipt           */
    /* --------------------------------------------------------------------- */
    useEffect(() => {
        const loadReceiptItems = async () => {
            if (!open) return;
            
            if (![6, 7,8].includes(doc?.transactionDocumentType)) return;
            try {
                const res = await DocumentService.GetDocumentById(
                    'PaymentAcceptances', client.caseId, doc.id, token);

                if (res.ok) {
                    const data = await res.json();          // expecting { receiptItems: {...} }

                    setWithholdingTax(data.withholdingTax ?? 0);
                    const receiptItems = Object.fromEntries(PAYMENT_GROUPS.map(({ key, type }) => [
                        key,
                        (data[key] ?? []).map(item => ({ ...item, type })),
                    ]));

                    setReceiptItems(receiptItems);
                }
            } catch (err) { console.error('Failed to fetch receipt items', err); }
        };
        loadReceiptItems();
    }, [open, doc, client, token]);

    if (!open) return null;




    // Merge doc, items, and account info into one object to send
    const handleGenerateClick = () => {
        if (isReceiptGenerate && linkAmountError) return;

        const itemsToGenerate = itemsEditable ? editableItems : (docItems || []);
        const {
            itemsAlreadyIncludeVat: detectedPriceIncludingVat,
            itemsTotal: generatedItemsTotal,
        } = computeItemTotals(
            itemsToGenerate,
            isReceiptGenerate ? linkAmount : (itemsEditable ? null : referenceAmount),
            withholdingTax,
            isMultiSource,
            editedPricing,
        );
        // Multi-source items are normalized per source document to VAT-inclusive prices.
        const priceIncludingVat = isMultiSource ? true : detectedPriceIncludingVat;
        const resolvedAmount = isReceiptGenerate
            ? linkAmount
            : (itemsEditable || isMultiSource)
                ? (generatedItemsTotal || referenceAmount)
                : (doc?.amount ?? referenceAmount);

        const sourceAllocations = isMultiSource && !isCancelAction
            ? buildSourceAllocations(itemsToGenerate, activeSourceDocs)
            : [];

        const mergedObject = {
            sourceAllocations,
            documentInfo: {
                ...doc,
                amount: resolvedAmount,
                // Keep lifecycle context for ReceiptItemsModal (cap / summary).
                originalAmount: doc?.originalAmount ?? doc?.amount,
                remainingAmount: isReceiptGenerate ? availableAmount : doc?.remainingAmount,
                appliedAmount: doc?.appliedAmount,
                documentDate: selectedDocumentDate?.toISOString() || doc?.documentDate,
                vat: (doc?.vat !== undefined && doc?.vat !== null) ? doc.vat : 100,
                sourceDocuments: isMultiSource ? activeSourceDocs : doc?.sourceDocuments,
            },
            items: itemsToGenerate.map(item => {
                const match = clientItems?.find(
                    (ci) => String(ci.id) === String(item.itemId)
                );
                const {
                    sourceDocumentId,
                    sourceDocumentReference,
                    sourcePriceIncludingVAT,
                    _remaining,
                    ...itemRest
                } = item;
                return {
                    ...itemRest,
                    makat: item.makat ?? match?.makat ?? '—',
                    name: item.name ?? match?.name ?? '—',
                    includeVat: item.includeVat !== false && item.IncludeVat !== false,
                };
            }),
            receiptItems,
            accountCardInfo,
            pendingAction,
            priceIncludingVat,
            invoiceWithholdingTax: withholdingTax,
            description: description,
            noNeedforConfirmationNumber: showNoNeedConfirmationCheckbox ? noNeedforConfirmationNumber : false,
            alsoCancelSourceDocuments: showAlsoCancelSources ? alsoCancelSourceDocuments : false,
        };

        onGenerate(mergedObject);
    };


    const handleClose = () => {
        setReceiptItems({})
        setWithholdingTax(0)
        setSelectedDocumentDate(null)
        setDescription('')
        setNoNeedforConfirmationNumber(false)
        setPreviewIndex(0)
        setAlsoCancelSourceDocuments(false)
        setLinkAmountInput('')
        setEditableItems([])
        onClose()
    }

    const showsRemainingHints = itemsEditable && editableItems.some((item) => item._remaining);
    const itemColumnCount = (isMultiSource ? 8 : 7) + (itemsEditable ? 1 : 0);
    const itemsCount = activeItems?.length || 0;
    const lockItemsTableToThreeRows = itemsCount > ITEMS_TABLE_VISIBLE_ROWS;
    // Rows with a remaining-quantity pill are taller.
    const itemsTableMaxHeight = showsRemainingHints
        ? ITEMS_TABLE_HEAD_PX + ITEMS_TABLE_VISIBLE_ROWS * (ITEMS_TABLE_ROW_PX + 20)
        : ITEMS_TABLE_MAX_HEIGHT;
    const itemsTableScrollSx = {
        ...itemsTableScrollBaseSx,
        maxHeight: itemsTableMaxHeight,
        // After 3 items: freeze height at 3 rows and scroll; otherwise hug content.
        ...(lockItemsTableToThreeRows
            ? { height: itemsTableMaxHeight }
            : { height: 'auto' }),
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '96%', md: '92%', lg: '88%' },
                    maxWidth: 1480,
                    // Grow/shrink with form + items table; never taller than the viewport.
                    height: 'auto',
                    maxHeight: { xs: '96vh', lg: '94vh' },
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    borderRadius: 3,
                    boxShadow: 8,
                    p: { xs: 1.5, md: 2 },
                    direction,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1.5,
                        flexShrink: 0,
                    }}
                    className={direction}
                >
                    <Typography variant="h5" fontWeight={600}>
                        {t(pendingAction?.label)}
                    </Typography>
                    <Tooltip title={t('Close')}>
                        <IconButton onClick={handleClose} sx={{ color: '#304FFF' }}>
                            <CancelIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Content — side by side on wide screens (PDF stretches to the form); stacked below lg */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', lg: 'row' },
                        alignItems: 'stretch',
                        gap: 2,
                        flex: '1 1 auto',
                        minHeight: 0,
                        overflow: 'auto',
                        ...themedScrollbarSx,
                    }}
                >
                    {/* PDF Preview */}
                    <Box
                        sx={{
                            flex: { xs: '0 0 auto', lg: '0 0 calc(46% - 8px)' },
                            width: { xs: '100%', lg: 'auto' },
                            minWidth: 0,
                            // Stacked: a fixed preview height; side by side it stretches to the form.
                            height: { xs: 380, sm: 460, lg: 'auto' },
                            minHeight: 360,
                            borderRadius: 2,
                            border: '1px solid #E0E0E0',
                            overflow: 'hidden',
                            boxShadow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: '#F7F8FC',
                            alignSelf: 'stretch',
                        }}
                    >
                        {isMultiSource && previewDocs.length > 1 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    px: 1.5,
                                    py: 1,
                                    borderBottom: '1px solid #E0E0E0',
                                    backgroundColor: '#F5F7FF',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" fontWeight={600}>
                                        {t(sourceDocumentsLabelKey)}
                                    </Typography>
                                    {previewDocs.map((sourceDoc, index) => (
                                        <Chip
                                            key={sourceDoc.id ?? index}
                                            size="small"
                                            label={sourceDoc.reference || index + 1}
                                            color={previewIndex === index ? 'primary' : 'default'}
                                            variant={previewIndex === index ? 'filled' : 'outlined'}
                                            onClick={() => setPreviewIndex(index)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <IconButton
                                        size="small"
                                        disabled={previewIndex <= 0}
                                        onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
                                    >
                                        <ArrowBackIcon sx={{ color: '#304FFF' }} />
                                    </IconButton>
                                    <Typography variant="caption">
                                        {previewIndex + 1} / {previewDocs.length}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        disabled={previewIndex >= previewDocs.length - 1}
                                        onClick={() => setPreviewIndex((i) => Math.min(previewDocs.length - 1, i + 1))}
                                    >
                                        <ArrowForwardIcon sx={{ color: '#304FFF' }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        )}
                        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
                            <Iframe
                                url={previewUrl}
                                width="100%"
                                height="100%"
                                display="block"
                                position="absolute"
                                overflow="hidden"
                                styles={{
                                    border: 'none',
                                    inset: 0,
                                    width: '100%',
                                    height: '100%',
                                }}
                                title={isMultiSource
                                    ? `${t(sourceDocumentsLabelKey)} ${currentPreviewDoc?.reference || previewIndex + 1}`
                                    : 'PDF Preview'}
                            />
                        </Box>
                    </Box>

                    {/* Document Info + Items — drives modal height */}
                    <Box
                        sx={{
                            // Stacked: natural height so the content area scrolls instead of clipping the form.
                            flex: { xs: '0 0 auto', lg: '1 1 0' },
                            width: { xs: '100%', lg: 'auto' },
                            minWidth: 0,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            overflow: { xs: 'visible', lg: 'hidden' },
                            height: 'auto',
                            alignSelf: { xs: 'stretch', lg: 'flex-start' },
                            maxHeight: { xs: 'none', lg: '100%' },
                        }}
                        className={direction}
                    >
                        {/* Document Info — stays fixed; only the items table scrolls */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, flexShrink: 0 }}>
                            <InfoItem label={t('Reference')} value={displayReference} />
                            {
                                pendingAction.value === 'update_items' ?
                                    <InfoItem label={t('documentDate')} value={formatDate(doc?.documentDate)} />
                                    :
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.25,
                                            py: 0.55,
                                            maxWidth: 230,
                                            width: '100%',
                                            mx: 'auto',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#8A90A6',
                                                fontWeight: 600,
                                                fontSize: '0.85rem',
                                                letterSpacing: '0.04em',
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            {t('documentDate')}
                                        </Typography>
                                        <CustomDatePicker
                                            date={selectedDocumentDate}
                                            onDateChange={setSelectedDocumentDate}
                                            height="40px"
                                            error={!selectedDocumentDate}
                                            inputSx={{
                                                border: selectedDocumentDate
                                                    ? '2px solid #E5E7F6'
                                                    : '1px solid #d32f2f',
                                                '&:focus-within': {
                                                    border: selectedDocumentDate
                                                        ? '2px solid #304FFF'
                                                        : '1px solid #d32f2f',
                                                },
                                            }}
                                        />
                                    </Box>

                            }
                            {
                                Number(withholdingTax) !== 0 &&
                                <InfoItem label={t('WithholdingTax')} value={withholdingTax} />
                            }
                            <InfoItem label={t('CustomerName')} value={doc?.accountCodeName} />
                            {isMultiSource && (
                                <InfoItem
                                    label={t(sourceDocumentsLabelKey)}
                                    value={activeSourceDocs.map((d) => d.reference).join(', ')}
                                />
                            )}
                            <InfoItem
                                label={t('Amount')}
                                value={beautifyNumber(displayAmount)}
                            />

                            {accountCardInfo && (
                                <>
                                    <InfoItem label={t('CompanyId')} value={accountCardInfo?.accountCaseId} />
                                    <InfoItem label={t('Email')} value={accountCardInfo?.email} />
                                    <InfoItem label={t('phoneNumber')} value={accountCardInfo?.phoneNumber} />
                                    <InfoItem label={t('Location')} value={accountCardInfo?.location} />
                                </>
                            )}

                        </Box>

                        {showAllocationSummary && allocationProjection && (
                            <Box
                                dir={direction}
                                sx={{
                                    mt: 1.5,
                                    p: 1.25,
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 2.5,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #E5E7F6',
                                    borderRadius: 2,
                                    backgroundColor: '#F8F9FF',
                                    flexShrink: 0,
                                }}
                            >
                                <InfoItem
                                    align="center"
                                    dense
                                    label={t('LinkDocumentsAvailableAmount')}
                                    value={formatLinkAmount(allocationProjection.available)}
                                />
                                <Box
                                    sx={{
                                        minWidth: 120,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#8A90A6',
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            letterSpacing: '0.03em',
                                            textTransform: 'uppercase',
                                            display: 'block',
                                            mb: 0.35,
                                            textAlign: 'center',
                                            width: '100%',
                                        }}
                                    >
                                        {t('LinkDocumentsTotalToLink')}
                                    </Typography>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={linkAmountInput}
                                        onChange={(e) => setLinkAmountInput(e.target.value)}
                                        error={linkAmountError}
                                        inputProps={{
                                            min: 0,
                                            max: availableAmount,
                                            step: '0.01',
                                            style: { textAlign: 'center', fontSize: '0.8rem' },
                                            onWheel: (e) => e.target.blur(),
                                        }}
                                        sx={{
                                            width: 120,
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#fff',
                                                borderRadius: '8px',
                                                fontSize: '0.8rem',
                                                '& input': { py: 0.75 },
                                            },
                                        }}
                                    />
                                </Box>
                                <InfoItem
                                    align="center"
                                    dense
                                    label={t('LinkDocumentsRemainingAfter')}
                                    value={formatLinkAmount(allocationProjection.remainingAfter)}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#8A90A6',
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            letterSpacing: '0.03em',
                                            textTransform: 'uppercase',
                                            display: 'block',
                                            mb: 0.35,
                                            textAlign: 'center',
                                            width: '100%',
                                        }}
                                    >
                                        {t('LinkDocumentsResultingStatus')}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        color={
                                            getStatusClassName({
                                                processingStatus: allocationProjection.sourceStatus,
                                                transactionDocumentType: TAX_INVOICE_TYPE,
                                            }) === 'processed'
                                                ? 'success'
                                                : 'warning'
                                        }
                                        label={t(getStatusLabelKey({
                                            processingStatus: allocationProjection.sourceStatus,
                                            transactionDocumentType: TAX_INVOICE_TYPE,
                                        }))}
                                        sx={{ fontSize: '0.7rem', height: 22 }}
                                    />
                                </Box>
                                {linkAmountError && (
                                    <Typography variant="caption" color="error" sx={{ width: '100%', textAlign: 'center' }}>
                                        {linkAmount <= 0
                                            ? t('LinkDocumentsAmountPositive')
                                            : t('LinkDocumentsTotalExceedsAvailable')}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {/* Description Field */}
                        <Box sx={{ mt: 1, flexShrink: 0 }}>
                            <Typography variant="body2" color="text.secondary" mb={0.5} sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                {t('Description')}
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={1}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={t('Enter description...')}
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        backgroundColor: 'var(--bg-input, #F5F5F6)',
                                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                        border: '2px solid #E5E7F6',
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#C5C8E0',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#304FFF',
                                            borderWidth: '2px',
                                        },
                                    },
                                }}
                                InputProps={{
                                    style: {
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                    },
                                }}
                            />
                        </Box>

                        {showNoNeedConfirmationCheckbox && (
                            <Box
                                className={`document-mode-toggle${noNeedforConfirmationNumber ? ' is-checked' : ''}`}
                                sx={{ mt: 0.5, alignSelf: 'flex-start', flexShrink: 0 }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={noNeedforConfirmationNumber}
                                            onChange={(e) => setNoNeedforConfirmationNumber(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label={t('NoNeedForConfirmationNumber')}
                                />
                            </Box>
                        )}

                        {showAlsoCancelSources && (
                            <Box
                                className={`document-mode-toggle${alsoCancelSourceDocuments ? ' is-checked' : ''}`}
                                sx={{ mt: 0.5, alignSelf: 'flex-start', flexShrink: 0 }}
                            >
                                {/* <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={alsoCancelSourceDocuments}
                                            onChange={(e) => setAlsoCancelSourceDocuments(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label={t('AlsoCancelSourceDocuments')}
                                /> */}
                                <Typography variant="caption" sx={{ display: 'block', color: '#8A90A6', pl: 4, pr: 4 }}>
                                    {liveConversionChildren
                                        .map((child) => {
                                            const typeName = Constants.TransactionDocuments?.[child.transactionDocumentType] || t('Document');
                                            return child.reference
                                                ? `${typeName} ${child.reference}`
                                                : typeName;
                                        })
                                        .join(', ')}
                                </Typography>
                            </Box>
                        )}

                        {isPartlyInvoiced && (
                            <Box
                                dir={direction}
                                sx={{
                                    flexShrink: 0,
                                    textAlign: 'start',
                                    border: '1px solid #FED7AA',
                                    background: 'linear-gradient(180deg, #FFF7ED 0%, #FFFBF5 100%)',
                                    borderRadius: 3,
                                    p: 1.5,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.25,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            borderRadius: '50%',
                                            backgroundColor: '#FFEDD5',
                                            color: '#EA580C',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />
                                    </Box>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#9A3412', flex: 1, minWidth: 0 }}>
                                        {t('GeneratePartlyInvoicedTitle')}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                    <Box sx={remainingStatTileSx}>
                                        <Typography variant="caption" sx={{ color: '#8A90A6', fontWeight: 600 }}>
                                            {t('GenerateRemainingToInvoice')}
                                        </Typography>
                                        <Typography sx={{ color: '#C2410C', fontWeight: 800, fontSize: '1.25rem', lineHeight: 1.2 }}>
                                            {remainingInfo?.remainingAmount != null
                                                ? formatLinkAmount(remainingInfo.remainingAmount)
                                                : '—'}
                                        </Typography>
                                    </Box>
                                    <Box sx={remainingStatTileSx}>
                                        <Typography variant="caption" sx={{ color: '#8A90A6', fontWeight: 600 }}>
                                            {t('GenerateAlreadyInvoiced')}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 0.75 }}>
                                            <Typography sx={{ color: '#1F2937', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
                                                {invoicedAmount != null ? formatLinkAmount(invoicedAmount) : '—'}
                                            </Typography>
                                            {remainingInfo?.originalAmount != null && (
                                                <Typography variant="caption" sx={{ color: '#8A90A6' }}>
                                                    {`${t('GenerateOf')} ${formatLinkAmount(remainingInfo.originalAmount)}`}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#EA580C', flexShrink: 0 }} />
                                    <Typography variant="caption" sx={{ color: '#9A3412' }}>
                                        {t('GenerateRemainingItemsHint')}
                                    </Typography>
                                </Box>

                                {fullyInvoicedItems.length > 0 && (
                                    <Box sx={{ borderTop: '1px dashed #FDBA74', pt: 1 }}>
                                        <Typography variant="caption" sx={{ color: '#8A90A6', fontWeight: 600, display: 'block', mb: 0.75 }}>
                                            {`${t('GenerateFullyInvoicedItems')} (${fullyInvoicedItems.length})`}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 0.75,
                                                maxHeight: 76,
                                                overflowY: 'auto',
                                                ...themedScrollbarSx,
                                            }}
                                        >
                                            {fullyInvoicedItems.map((item, index) => (
                                                <Box
                                                    key={`invoiced-${index}`}
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        maxWidth: '100%',
                                                        px: 1,
                                                        py: 0.375,
                                                        borderRadius: 999,
                                                        backgroundColor: '#fff',
                                                        border: '1px solid #BBF7D0',
                                                    }}
                                                >
                                                    <CheckCircleIcon sx={{ fontSize: 15, color: '#16A34A', flexShrink: 0 }} />
                                                    <Typography
                                                        variant="caption"
                                                        noWrap
                                                        sx={{ color: '#6B7280', textDecoration: 'line-through', minWidth: 0 }}
                                                    >
                                                        {[item.name || item.line?.name, item.makat || item.line?.makat].filter(Boolean).join(' · ') || '—'}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#15803D', fontWeight: 700, flexShrink: 0 }}>
                                                        {`× ${Number(item.line?.quantity) || 0}`}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Item Table — rows scroll; totals stay fixed below */}
                        {showItemLines && (
                        <>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ flexShrink: 0 }}>
                            {t('ItemList')}
                        </Typography>

                        <Box
                            dir={direction}
                            sx={{
                                flex: '0 1 auto',
                                width: '100%',
                                borderRadius: 3,
                                border: '1px solid #E5E7F6',
                                boxShadow: 1,
                                backgroundColor: '#fff',
                                overflow: 'hidden',
                            }}
                        >
                            <TableContainer sx={itemsTableScrollSx}>
                                <StyledTable size="small">
                                    <TableHead>
                                        <TableRow>
                                            {isMultiSource && (
                                                <TableCell sx={{ width: '10%' }}>{t(sourceDocumentsLabelKey)}</TableCell>
                                            )}
                                            <TableCell sx={{ width: itemsEditable ? '9%' : '10%' }}>{t('Makat')}</TableCell>
                                            <TableCell sx={{ width: showsRemainingHints ? '26%' : (itemsEditable ? '30%' : '31%') }}>{t('Name')}</TableCell>
                                            <TableCell sx={{ width: showsRemainingHints ? '13%' : '9%' }}>{t('Quantity')}</TableCell>
                                            <TableCell sx={{ width: '12%' }}>{t('Price')}</TableCell>
                                            <TableCell sx={{ width: '9%' }}>{t('Discount')} (%)</TableCell>
                                            <TableCell sx={{ width: '12%' }}>{t('DiscountAmount')}</TableCell>
                                            <TableCell sx={{ width: '12%' }}>{t('Total')}</TableCell>
                                            {itemsEditable && (
                                                <TableCell align="center" sx={{ width: '6%' }}></TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {itemsCount > 0 ? (
                                            activeItems.map((item, index) => {
                                                const matchedClientItem = selectableClientItems.find(
                                                    (ci) => String(ci.id) === String(item.itemId)
                                                ) || selectableClientItems.find(
                                                    (ci) => item.makat && String(ci.makat) === String(item.makat)
                                                );
                                                const selectedFilterValue = resolveInventoryFilterValue(item);

                                                const price = parseFloat(item.price) || 0;
                                                const quantity = parseFloat(item.quantity) || 0;
                                                const discountPercent = parseFloat(item.discount) || 0;

                                                const baseAmount = price * quantity;
                                                const discountAmount = (baseAmount * discountPercent) / 100;
                                                const totalPrice = baseAmount - discountAmount;
                                                const sourceRef = item.sourceDocumentReference
                                                    || activeSourceDocs.find((d) => String(d.id) === String(item.sourceDocumentId))?.reference
                                                    || '—';
                                                return (
                                                    <TableRow
                                                        key={item._key || `row-${index}`}
                                                        sx={itemsEditable && item._remaining
                                                            ? { '&& > .MuiTableCell-body': { pt: '10px', pb: '30px' } }
                                                            : undefined}
                                                    >
                                                        {isMultiSource && (
                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    label={sourceRef}
                                                                    color="primary"
                                                                    variant="outlined"
                                                                    onClick={() => {
                                                                        const idx = activeSourceDocs.findIndex(
                                                                            (d) => String(d.id) === String(item.sourceDocumentId)
                                                                                || String(d.reference) === String(sourceRef)
                                                                        );
                                                                        if (idx >= 0) setPreviewIndex(idx);
                                                                    }}
                                                                    sx={{ cursor: 'pointer', fontWeight: 600 }}
                                                                />
                                                            </TableCell>
                                                        )}
                                                        <TableCell>
                                                            {item.makat || matchedClientItem?.makat || '—'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {itemsEditable ? (
                                                                <Box sx={{ position: 'relative', zIndex: 0, minWidth: 0 }}>
                                                                    <AutocompleteSelectFilter
                                                                        options={inventoryFilterOptions}
                                                                        selectedValues={selectedFilterValue}
                                                                        onChange={(_, selected) => applyClientItemToRow(index, selected)}
                                                                        placeholder={t('InventoryItem')}
                                                                        isMultiple={false}
                                                                        direction={direction}
                                                                        searchable
                                                                        clearable
                                                                        portalMenu
                                                                        portalZIndex={1600}
                                                                        menuMaxHeight={180}
                                                                    />
                                                                </Box>
                                                            ) : (item.name || matchedClientItem?.name || '—')}
                                                        </TableCell>
                                                        <TableCell>
                                                            {itemsEditable ? (
                                                                <Box sx={{ position: 'relative' }}>
                                                                    <TextField
                                                                        size="small"
                                                                        variant="standard"
                                                                        type="number"
                                                                        fullWidth
                                                                        className={fieldStyles.styleInput}
                                                                        value={item.quantity ?? ''}
                                                                        onChange={(e) => updateEditableItem(index, { quantity: e.target.value })}
                                                                        InputProps={comboInputProps}
                                                                        inputProps={{
                                                                            min: 0,
                                                                            step: 'any',
                                                                            style: { textAlign: 'center' },
                                                                            onWheel: (e) => e.target.blur(),
                                                                        }}
                                                                    />
                                                                    {!!item._remaining && (() => {
                                                                        const left = Number(item._remaining.remainingQuantity) || 0;
                                                                        const exceeds = quantity > left + 0.0001;
                                                                        return (
                                                                            <Tooltip title={exceeds ? t('GenerateExceedsRemaining') : ''} placement="bottom">
                                                                                <Box
                                                                                    component="span"
                                                                                    sx={{
                                                                                        position: 'absolute',
                                                                                        top: 'calc(100% + 4px)',
                                                                                        left: 0,
                                                                                        right: 0,
                                                                                        mx: 'auto',
                                                                                        width: 'fit-content',
                                                                                        height: 18,
                                                                                        px: 1,
                                                                                        display: 'inline-flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        boxSizing: 'border-box',
                                                                                        borderRadius: 999,
                                                                                        fontSize: '0.6875rem',
                                                                                        lineHeight: 1,
                                                                                        whiteSpace: 'nowrap',
                                                                                        fontWeight: 600,
                                                                                        color: exceeds ? '#B91C1C' : '#C2410C',
                                                                                        backgroundColor: exceeds ? '#FEE2E2' : '#FFF1E6',
                                                                                        border: `1px solid ${exceeds ? '#FCA5A5' : '#FED7AA'}`,
                                                                                    }}
                                                                                >
                                                                                    {`${t('GenerateRemainingQty')} ${left} ${t('GenerateOf')} ${Number(item._remaining.quantity) || 0}`}
                                                                                </Box>
                                                                            </Tooltip>
                                                                        );
                                                                    })()}
                                                                </Box>
                                                            ) : quantity}
                                                        </TableCell>
                                                        <TableCell>
                                                            {beautifyNumber(price)}
                                                        </TableCell>
                                                        <TableCell>
                                                            {itemsEditable ? (
                                                                <TextField
                                                                    size="small"
                                                                    variant="standard"
                                                                    type="number"
                                                                    fullWidth
                                                                    className={fieldStyles.styleInput}
                                                                    value={item.discount ?? 0}
                                                                    onChange={(e) => updateEditableItem(index, { discount: e.target.value })}
                                                                    InputProps={comboInputProps}
                                                                    inputProps={{
                                                                        min: 0,
                                                                        max: 100,
                                                                        step: 'any',
                                                                        style: { textAlign: 'center' },
                                                                        onWheel: (e) => e.target.blur(),
                                                                    }}
                                                                />
                                                            ) : discountPercent}
                                                        </TableCell>
                                                        <TableCell>{beautifyNumber(discountAmount)}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{beautifyNumber(totalPrice)}</TableCell>
                                                        {itemsEditable && (
                                                            <TableCell align="center" sx={{ px: 0.25 }}>
                                                                <Tooltip title={t('Delete')}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => removeEditableItem(index)}
                                                                        sx={{ color: '#d32f2f', p: 0.35 }}
                                                                    >
                                                                        <DeleteOutlineIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={itemColumnCount} align="center">
                                                    {t('NoItemsFound')}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </StyledTable>
                            </TableContainer>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                    px: 1.5,
                                    py: 1,
                                    borderTop: '1px solid #E5E7F6',
                                    backgroundColor: '#FAFBFF',
                                    flexShrink: 0,
                                }}
                            >
                                {withholdingTax !== 0 && (
                                    <>
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#d32f2f' }}>
                                            {t('WithholdingTax')}: {beautifyNumber(withholdingTax)}
                                        </Typography>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                                            {t('TotalAfterWithholding')}: {beautifyNumber(netTotalAfterWithholding)}
                                        </Typography>
                                    </>
                                )}
                                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                                    {itemsAlreadyIncludeVat
                                        ? t('Total')
                                        : `${t('Total')} (${t('Including VAT')})`}
                                    {': '}
                                    {beautifyNumber(itemsTotal)}
                                </Typography>
                            </Box>
                        </Box>
                        </>
                        )}

                        {showPaymentLines && (
                            <>
                                <Typography variant="subtitle1" fontWeight={600} sx={{ flexShrink: 0 }}>
                                    {t('GeneratePaymentsTitle')}
                                </Typography>

                                <Box
                                    dir={direction}
                                    sx={{
                                        flex: '0 1 auto',
                                        width: '100%',
                                        borderRadius: 3,
                                        border: '1px solid #E5E7F6',
                                        boxShadow: 1,
                                        backgroundColor: '#fff',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <TableContainer
                                        sx={{
                                            ...itemsTableScrollBaseSx,
                                            maxHeight: ITEMS_TABLE_MAX_HEIGHT,
                                            height: paymentRows.length > ITEMS_TABLE_VISIBLE_ROWS ? ITEMS_TABLE_MAX_HEIGHT : 'auto',
                                        }}
                                    >
                                        <StyledTable size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ width: '20%' }}>{t('PaymentMethod')}</TableCell>
                                                    <TableCell sx={{ width: '16%' }}>{t('PaymentDate')}</TableCell>
                                                    <TableCell sx={{ width: '46%' }}>{t('Details')}</TableCell>
                                                    <TableCell sx={{ width: '18%' }}>{t('Amount')}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {paymentRows.length > 0 ? paymentRows.map((line) => (
                                                    <TableRow key={line._key}>
                                                        <TableCell>{t(line.labelKey)}</TableCell>
                                                        <TableCell>{formatDate(line.paymentDate) || '—'}</TableCell>
                                                        <TableCell>{describePaymentLine(line) || '—'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{beautifyNumber(Number(line.amount) || 0)}</TableCell>
                                                    </TableRow>
                                                )) : (
                                                    <TableRow>
                                                        <TableCell colSpan={4} align="center">
                                                            {t('GeneratePaymentsEmpty')}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </StyledTable>
                                    </TableContainer>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                            px: 1.5,
                                            py: 1,
                                            borderTop: '1px solid #E5E7F6',
                                            backgroundColor: '#FAFBFF',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {withholdingTax !== 0 && (
                                            <>
                                                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#d32f2f' }}>
                                                    {t('WithholdingTax')}: {beautifyNumber(withholdingTax)}
                                                </Typography>
                                                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                                                    {t('Total')}: {beautifyNumber(roundAmount(paymentsTotal + (Number(withholdingTax) || 0)))}
                                                </Typography>
                                            </>
                                        )}
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                                            {t('GeneratePaymentsTotal')}: {beautifyNumber(paymentsTotal)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </>
                        )}

                        <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5 }}>
                            {itemsEditable && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        onClick={addEditableItem}
                                        startIcon={<AddIcon />}
                                        sx={{
                                            height: 36,
                                            px: 1.75,
                                            borderRadius: '8px',
                                            background: '#304fff',
                                            color: '#fff',
                                            fontSize: 13,
                                            fontWeight: 700,
                                            textTransform: 'none',
                                            '&:hover': { background: '#2340e6' },
                                        }}
                                    >
                                        {t('AddItem')}
                                    </Button>
                                </Box>
                            )}

                            {pendingAction?.value !== 'update_items' && (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleGenerateClick}
                                    disabled={!selectedDocumentDate || (isReceiptGenerate && (linkAmountError || availableAmount <= 0))}
                                    sx={{
                                        width: '100%',
                                        backgroundColor: '#304FFF',
                                        color: '#fff',
                                        fontWeight: 600,
                                        px: 4,
                                        py: 1.25,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        '&:hover': {
                                            backgroundColor: '#1e3de6',
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: '#B0B0B0',
                                        },
                                    }}
                                >
                                    {t(pendingAction?.label)}
                                </Button>
                            )}
                        </Box>

                    </Box>
                </Box>
            </Box>
        </Modal >
    );
}

// Helper for label + value pairs
const InfoItem = ({ label, value, align = 'start', dense = false }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: align === 'center' ? 'center' : 'stretch',
            gap: dense ? 0.1 : 0.15,
            py: dense ? 0.25 : 0.50,
            borderBottom: align === 'center' ? 'none' : '1px solid #EEF0F7',
            minWidth: 0,
            textAlign: align === 'center' ? 'center' : 'inherit',
        }}
    >
        <Typography
            variant="caption"
            sx={{
                color: '#8A90A6',
                fontWeight: 600,
                fontSize: dense ? '0.7rem' : '0.85rem',
                letterSpacing: dense ? '0.03em' : '0.04em',
                textTransform: 'uppercase',
                textAlign: align === 'center' ? 'center' : 'inherit',
                width: '100%',
            }}
        >
            {label}
        </Typography>
        <Typography
            variant="body2"
            sx={{
                fontWeight: 700,
                fontSize: dense ? '0.8rem' : undefined,
                color: 'var(--xtram-mirage, #1C1C28)',
                wordBreak: 'break-word',
                textAlign: align === 'center' ? 'center' : 'inherit',
                width: '100%',
            }}
            title={typeof value === 'string' || typeof value === 'number' ? String(value) : undefined}
        >
            {value || '—'}
        </Typography>
    </Box>
);

export default GenerateDocumentModal;
